/**
 * useAnoMcpNudge — informational "Connect Claude Code to Ano" toast.
 *
 * Mirrors `useCliVersionNudge` but for the Claude Code MCP attachment
 * rather than the local `ano` CLI version. Runs `claude mcp list` once
 * per workspace mount via the Electron IPC bridge, classifies the
 * result, and fires a sticky Sonner toast (`duration: Infinity`) when
 * action is needed.
 *
 * Web runtime: no-op (no `claude` binary).
 * Electron + ano not in MCP list: `missing`   → "Connect" action types `claude mcp add ano …` into the Shell.
 * Electron + ano listed but disconnected: `needs_auth` → guides to /mcp inside a Claude session.
 * Electron + claude binary missing: `claude_missing` → "Install" action opens claude.com/claude-code.
 * Electron + connected: no-op.
 *
 * Dismissed via X → remembered per status in localStorage so the toast
 * doesn't refire on every page mount once acknowledged. Status changes
 * (e.g. needs_auth → missing) re-show, since the dismissal is keyed on
 * the prior status value.
 *
 * Replaces the previous in-page banner — toasts are app-wide and stay
 * stylistically consistent with the rest of the app's notification UX.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { isElectronRuntime } from "@/lib/logout";
import { API_URL } from "@/lib/config";

export type AnoMcpStatus =
  | "connected"
  | "needs_auth"
  | "missing"
  | "claude_missing"
  | "unknown";

declare global {
  interface Window {
    __electronApi?: {
      // Defined alongside the other __electronApi members in
      // useCliVersionNudge.ts; both files declare partial interfaces
      // that TypeScript merges. Keep the names identical or you'll
      // hit "subsequent property declarations must have the same type".
      anoCliGetVersion?: () => Promise<{
        version: string | null;
        error?: string;
      }>;
      anoSkillGetVersion?: () => Promise<{
        version: string | null;
        error?: string;
      }>;
      claudeCheckAnoMcp?: () => Promise<{
        status: AnoMcpStatus;
        url?: string;
        error?: string;
      }>;
    };
  }
}

const TOAST_ID = "ano-mcp-nudge";
const DISMISS_KEY = "ano-mcp-nudge-dismissed";

function isDismissedFor(status: AnoMcpStatus): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === status;
  } catch {
    return false;
  }
}

function rememberDismissal(status: AnoMcpStatus): void {
  try {
    localStorage.setItem(DISMISS_KEY, status);
  } catch {
    /* ignore storage failures */
  }
}

function sendToShell(cmd: string): void {
  window.dispatchEvent(new CustomEvent("terminal:open"));
  window.dispatchEvent(
    new CustomEvent("terminal:rerun", {
      detail: { command: cmd, autoExecute: false },
    }),
  );
}

export function useAnoMcpNudge(enabled: boolean): void {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !isElectronRuntime() || firedRef.current) return;
    let cancelled = false;
    void (async () => {
      const r = await window.__electronApi
        ?.claudeCheckAnoMcp?.()
        .catch(() => null);
      if (cancelled) return;
      const status: AnoMcpStatus = r?.status ?? "unknown";
      // Bail on "claude_missing" as well as "connected" / "unknown".
      // Our `execFile("claude", ...)` probe can't reliably detect a
      // GUI-Electron-spawned PATH that's missing the user's install
      // location, so a "missing" verdict has been firing on users who
      // genuinely DO have Claude Code (see report 2026-05-20). The
      // false-positive nag is worse UX than skipping a genuine
      // recommend-to-install — users with no Claude Code will learn
      // about it elsewhere; users with it shouldn't be nagged.
      if (
        status === "connected" ||
        status === "unknown" ||
        status === "claude_missing"
      )
        return;
      if (isDismissedFor(status)) return;
      firedRef.current = true;

      const mcpUrl = `${API_URL.replace(/\/$/, "")}/mcp`;
      const onDismiss = () => rememberDismissal(status);

      if (status === "needs_auth") {
        toast.warning("Authenticate Ano in Claude Code", {
          id: TOAST_ID,
          description:
            "Claude Code has the Ano MCP added but isn't authenticated. Open a Claude Code session and run `/mcp` to sign in.",
          duration: Infinity,
          closeButton: true,
          onDismiss,
        });
        return;
      }

      // missing
      toast.warning("Connect Claude Code to Ano", {
        id: TOAST_ID,
        description:
          "Claude Code doesn't have the Ano MCP attached yet — it can't call your workspace's tools. Run this once to wire it up.",
        duration: Infinity,
        closeButton: true,
        action: {
          label: "Connect",
          onClick: () =>
            sendToShell(`claude mcp add ano --transport http --url ${mcpUrl}`),
        },
        onDismiss,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
}
